import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Info } from "lucide-react";
import { PsvMonitorIneligibilityReason } from "@api/shared-runtime/psv-monitoring";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";
import { EnableMonitoringDialog } from "./enable-monitoring-dialog";
import {
  FEATURE_NAME,
  INELIGIBILITY_COPY,
  MONITORING_COPY,
  MonitoringBadgeKind,
} from "./monitoring-constants";
import { formatMonitoringDate } from "./monitoring-format";
import type { ProviderMonitoringStatus } from "./monitoring-types";
import { MonitoringStatusBadge } from "./monitoring-status-badge";
import {
  coveredByLabels,
  directRules,
  joinNames,
  poolNames,
} from "./provider-monitoring-card/coverage";
import { MonitoringDetails } from "./provider-monitoring-card/monitoring-details";
import { StopMonitoringDialog } from "./provider-monitoring-card/stop-monitoring-dialog";

type Props = {
  organizationBusinessUnitId: string;
  providerUserId: string;
  expectedAffiliationId?: string;
  providerName: string;
};

/** "Monthly monitoring" card on the provider page's Verification tab. */
export function ProviderMonitoringCard({
  organizationBusinessUnitId,
  providerUserId,
  expectedAffiliationId,
  providerName,
}: Props) {
  const trpc = useTRPC();
  const query = useQuery(
    trpc.psv.monitoring.getProviderStatus.queryOptions({
      organizationBusinessUnitId,
      providerUserId,
      expectedAffiliationId,
    }),
  );
  if (query.isPending) {
    return (
      <Card role="status" aria-busy="true">
        <span className="sr-only">Loading {FEATURE_NAME.toLowerCase()}…</span>
        <CardContent aria-hidden className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-8 w-36" />
          </div>
          <Skeleton className="h-5 w-64 max-w-full" />
        </CardContent>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <CardContent className="space-y-2 p-4 text-sm">
          <p role="alert">Couldn't load monthly monitoring.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  const { eligibility } = query.data;
  // The feature flag is off for the BU: no monitoring entry point is shown.
  if (
    !eligibility.eligible &&
    eligibility.reason === PsvMonitorIneligibilityReason.FeatureDisabled
  ) {
    return null;
  }
  return (
    <MonitoringCardBody
      status={query.data}
      organizationBusinessUnitId={organizationBusinessUnitId}
      providerUserId={providerUserId}
      providerName={providerName}
    />
  );
}

type BodyProps = {
  status: ProviderMonitoringStatus;
  organizationBusinessUnitId: string;
  providerUserId: string;
  providerName: string;
};

function MonitoringCardBody(props: BodyProps) {
  const { eligibility, coveredBy } = props.status;
  if (!eligibility.eligible) {
    if (eligibility.reason === PsvMonitorIneligibilityReason.NotTargeted) {
      return <NotMonitoredCard {...props} />;
    }
    return <UnavailableCard reason={eligibility.reason} />;
  }
  if (coveredBy.length === 0) {
    return <NotMonitoredCard {...props} />;
  }
  return <MonitoredCard {...props} />;
}

/** Sits between the verification summary and the report: badge beside the
 * title, action at the right edge. */
function CardShell({
  badge,
  action,
  subtitle,
  children,
}: {
  badge: React.ReactNode;
  action?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <section
      aria-label={FEATURE_NAME}
      className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarClock className="size-4 text-primary" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-900">
            {FEATURE_NAME}
          </h3>
          {badge}
        </div>
        {action}
      </div>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      {children}
    </section>
  );
}

function UnavailableCard({
  reason,
}: {
  reason: Exclude<
    PsvMonitorIneligibilityReason,
    PsvMonitorIneligibilityReason.NotTargeted
  >;
}) {
  return (
    <CardShell
      badge={<MonitoringStatusBadge kind={MonitoringBadgeKind.Unavailable} />}
    >
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        {INELIGIBILITY_COPY[reason]}
      </p>
    </CardShell>
  );
}

function NotMonitoredCard({
  status,
  organizationBusinessUnitId,
  providerUserId,
  providerName,
}: BodyProps) {
  const [enableOpen, setEnableOpen] = useState(false);
  const canTurnOn = status.canManage && status.hasBillingPlan;
  return (
    <CardShell
      badge={<MonitoringStatusBadge kind={MonitoringBadgeKind.NotMonitored} />}
      subtitle={MONITORING_COPY.notMonitoredDescription}
      action={
        status.canManage ? (
          <Button
            size="sm"
            className="max-sm:h-11"
            disabled={!canTurnOn}
            onClick={() => setEnableOpen(true)}
          >
            {MONITORING_COPY.turnOn}
          </Button>
        ) : undefined
      }
    >
      {!status.canManage && (
        <p className="text-sm text-muted-foreground">
          {MONITORING_COPY.readOnly}
        </p>
      )}
      {status.canManage && !status.hasBillingPlan && (
        <p className="text-sm text-muted-foreground">
          {MONITORING_COPY.noBillingPlan}
        </p>
      )}
      {enableOpen && (
        <EnableMonitoringDialog
          organizationBusinessUnitId={organizationBusinessUnitId}
          subjectName={providerName}
          target={{ providerUserId }}
          onClose={() => setEnableOpen(false)}
        />
      )}
    </CardShell>
  );
}

function monitorSubtitle(monitor: ProviderMonitoringStatus["monitor"]) {
  if (!monitor) {
    return MONITORING_COPY.firstCheckPending;
  }
  const since = `Since ${formatMonitoringDate(monitor.startedAt)}`;
  if (!monitor.nextDueAt) {
    return since;
  }
  return `${since} · Next check ${formatMonitoringDate(monitor.nextDueAt)}`;
}

function MonitoredCard({
  status,
  organizationBusinessUnitId,
  providerUserId,
  providerName,
}: BodyProps) {
  const [stopOpen, setStopOpen] = useState(false);
  const direct = directRules(status.coveredBy);
  const pools = poolNames(status.coveredBy);
  const viaPoolOnly = direct.length === 0;
  return (
    <CardShell
      badge={<MonitoringStatusBadge kind={MonitoringBadgeKind.Monitored} />}
      subtitle={monitorSubtitle(status.monitor)}
      action={
        status.canManage && !viaPoolOnly ? (
          <Button
            variant="outline"
            size="sm"
            className="max-sm:h-11"
            onClick={() => setStopOpen(true)}
          >
            Stop monitoring
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-slate-900">Covered by</p>
        <ul className="text-sm text-muted-foreground">
          {coveredByLabels(status.coveredBy).map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </div>
      {status.scope && <MonitoringDetails scope={status.scope} />}
      {viaPoolOnly && (
        <Alert>
          <AlertDescription>
            Monitored through {joinNames(pools)}. Remove them from the pool or
            turn off the pool's monitoring.
          </AlertDescription>
        </Alert>
      )}
      {!status.canManage && (
        <p className="text-sm text-muted-foreground">
          {MONITORING_COPY.readOnly}
        </p>
      )}
      {stopOpen && (
        <StopMonitoringDialog
          organizationBusinessUnitId={organizationBusinessUnitId}
          providerUserId={providerUserId}
          providerName={providerName}
          programIds={direct.map((rule) => rule.programId)}
          // Stopping removes only direct targets, so every pool, including
          // one on a rule that also targets the provider directly, remains.
          remainingPoolNames={pools}
          onClose={() => setStopOpen(false)}
        />
      )}
    </CardShell>
  );
}
